/**
 * IMPORTADOR DE ITEMS DESDE EXCEL (PRESU-IMPORTADOR-PANEL-T1, D-143; contrato nexo-api 584, A #31573).
 *
 *   POST /presupuestos/plantilla/importar/vista-previa  (multipart: archivo .xlsx hasta 10 MB) -> ImportarVistaPreviaDto
 *   POST /presupuestos/plantilla/importar/confirmar     { filas: [...] } (1 a 1000)              -> ImportarConfirmadoDto
 *
 * La vista previa NO escribe. El dueno revisa cada fila (puede editar nombre, unidad, precio, seccion y paso, o quitarla)
 * y solo se guardan las MARCADAS. La seccion es SIEMPRE una de las que ya existen (o ninguna): nunca se crea desde el
 * archivo. Un precio vacio va como null ("Por definir"), nunca como 0. Si el nombre ya esta en Mis items, el dueno elige
 * usar ese producto (productoId) o crear otro. Con una fila invalida la API no guarda nada y dice cuales.
 *
 * @module application/presupuestos/importadorItems
 */

export const MAX_FILAS = 1000;
export const PASOS_IMPORTADOR = ['tipo', 'revision_obligatoria', 'complemento'];

const de = (o, ...ks) => { for (const k of ks) if (o && o[k] !== undefined && o[k] !== null) return o[k]; return undefined; };
const lista = (o, a, b) => (Array.isArray(o?.[a]) ? o[a] : Array.isArray(o?.[b]) ? o[b] : []);
const numONull = (v) => (v === undefined || v === null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));

/** ImportarVistaPreviaDto en camel o Pascal. */
export function leerVistaPrevia(data) {
  const d = data || {};
  return {
    archivo: String(de(d, 'archivo', 'Archivo') ?? ''),
    hoja: de(d, 'hoja', 'Hoja') ?? null,
    totalFilas: Number(de(d, 'totalFilas', 'TotalFilas')) || 0,
    conSeccion: Number(de(d, 'conSeccion', 'ConSeccion')) || 0,
    sinSeccion: Number(de(d, 'sinSeccion', 'SinSeccion')) || 0,
    duplicados: Number(de(d, 'duplicadosMisItems', 'DuplicadosMisItems')) || 0,
    secciones: lista(d, 'secciones', 'Secciones').map((s) => ({ id: String(de(s, 'id', 'Id') ?? ''), nombre: String(de(s, 'nombre', 'Nombre') ?? '') })).filter((s) => s.id),
    problemas: lista(d, 'problemas', 'Problemas').map(String),
    filas: lista(d, 'filas', 'Filas').map((f) => ({
      hoja: String(de(f, 'hoja', 'Hoja') ?? ''),
      fila: Number(de(f, 'fila', 'Fila')) || 0,
      descripcion: String(de(f, 'descripcion', 'Descripcion') ?? ''),
      unidad: de(f, 'unidad', 'Unidad') ?? '',
      cantidad: numONull(de(f, 'cantidad', 'Cantidad')),
      precio: numONull(de(f, 'precio', 'Precio')),
      seccionArchivo: de(f, 'seccionArchivo', 'SeccionArchivo') ?? '',
      seccionSugeridaId: de(f, 'seccionSugeridaId', 'SeccionSugeridaId') ? String(de(f, 'seccionSugeridaId', 'SeccionSugeridaId')) : '',
      productoExistenteId: de(f, 'productoExistenteId', 'ProductoExistenteId') ? String(de(f, 'productoExistenteId', 'ProductoExistenteId')) : '',
      productoExistente: String(de(f, 'productoExistente', 'ProductoExistente') ?? ''),
      avisos: lista(f, 'avisos', 'Avisos').map(String)
    }))
  };
}

/**
 * Lo que el dueno puede cambiar de una fila, con lo que sugiere la API. Una fila repetida dentro del mismo archivo
 * arranca DESMARCADA (si no, se guardaria dos veces); las demas, marcadas. Si el nombre ya esta en Mis items arranca
 * usando ese producto (no crea un duplicado).
 */
export function estadoInicial(f) {
  return {
    incluir: !f.avisos.includes('REPETIDO_EN_ARCHIVO'),
    descripcion: f.descripcion,
    unidad: f.unidad || '',
    precio: f.precio,
    seccionId: f.seccionSugeridaId || '',
    paso: 'complemento',
    usarExistente: !!f.productoExistenteId,
    productoExistenteId: f.productoExistenteId
  };
}

/** Por que una fila marcada no se puede mandar (null = esta bien). */
export function problemaDeFila(e, seccionesValidas) {
  if (!e.incluir) return null;
  if (!String(e.descripcion || '').trim()) return 'Falta el nombre.';
  if (e.seccionId && !seccionesValidas.has(e.seccionId)) return 'Esa seccion no existe en tu plantilla.';
  if (e.precio !== null && !(e.precio >= 0)) return 'El precio no es un numero valido.';
  if (!PASOS_IMPORTADOR.includes(e.paso)) return 'Paso no valido.';
  return null;
}

/**
 * El cuerpo de confirmar (camelCase): solo las marcadas, en el orden de la tabla. Devuelve tambien de que fila de la
 * tabla salio cada una, para pintar los errores que la API devuelve por indice (0 = la primera enviada).
 */
export function cuerpoConfirmar(estados) {
  const filas = [];
  const origen = [];
  estados.forEach((e, i) => {
    if (!e.incluir) return;
    const fila = { descripcion: String(e.descripcion).trim(), unidad: String(e.unidad || '').trim() || null, precio: e.precio, seccionId: e.seccionId || null, paso: e.paso };
    if (e.usarExistente && e.productoExistenteId) fila.productoId = e.productoExistenteId;
    filas.push(fila);
    origen.push(i);
  });
  return { filas, origen };
}

/** Errores por fila del 400/409 (extensions.filas en la raiz del ProblemDetails), traducidos a la fila de la tabla. */
export function erroresPorFila(res, origen) {
  const d = (res && res.details) || {};
  const crudos = Array.isArray(d.filas) ? d.filas : Array.isArray(d.extensions?.filas) ? d.extensions.filas : [];
  const porFila = new Map();
  for (const e of crudos) {
    const indice = Number(e.indice ?? e.Indice);
    const fila = origen[indice];
    if (fila === undefined) continue;
    porFila.set(fila, String(e.mensaje ?? e.Mensaje ?? e.codigo ?? e.Codigo ?? 'Fila con problemas'));
  }
  return porFila;
}

/** ImportarConfirmadoDto en camel o Pascal. */
export function leerConfirmado(data) {
  const d = data || {};
  return {
    filas: Number(de(d, 'filas', 'Filas')) || 0,
    creados: Number(de(d, 'productosCreados', 'ProductosCreados')) || 0,
    reusados: Number(de(d, 'productosReusados', 'ProductosReusados')) || 0,
    ubicaciones: Number(de(d, 'ubicacionesNuevas', 'UbicacionesNuevas')) || 0
  };
}

/** Lo que dice la pantalla de un aviso de la API. */
export function textoAviso(a) {
  return ({
    PRECIO_VACIO: 'Sin precio en el archivo',
    SECCION_SIN_CASAR: 'La sección del archivo no coincide con ninguna tuya',
    DUPLICADO_MIS_ITEMS: 'Ya está en Mis items',
    REPETIDO_EN_ARCHIVO: 'Repetido en el archivo'
  })[a] || a;
}
